import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Upload, ExternalLink, Copy, ChevronsUpDown, Check, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const LANGS = ['en', 'fr', 'es'];
const emptyTT = () => ({ en: '', fr: '', es: '' });
const TOTAL_STEPS = 5;

export default function MultiLocationLinkEditor() {
    const { id } = useParams();
    const { i18n, t } = useTranslation();
    const srcLang = LANGS.includes(i18n.language) ? i18n.language : 'en';
    const navigate = useNavigate();
    const isNew = id === 'new';
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [cards, setCards] = useState([]);
    const [linkId, setLinkId] = useState(isNew ? null : id);
    const [slug, setSlug] = useState(null);
    const [status, setStatus] = useState('draft');
    const [step, setStep] = useState(1);
    const justCreatedRef = React.useRef(false);

    const [name, setName] = useState('');
    const [style, setStyle] = useState('visible_buttons');
    const [content, setContent] = useState({ heading: emptyTT(), subtext: emptyTT() });
    const [design, setDesign] = useState({
        logo_url: null, logo_size: 'medium', background_image_url: null,
        background_position: 'center', background_behavior: 'cover', background_color: '',
        display_mode: 'auto', card_style: 'frosted', button_style: 'solid',
    });
    const [locations, setLocations] = useState([]);
    const [entryCardIds, setEntryCardIds] = useState([]);

    const cardById = useMemo(() => Object.fromEntries(cards.map((c) => [c.card_id, c])), [cards]);
    const [entryQuery, setEntryQuery] = useState('');
    const cardMatches = (c, q) => {
        const s = q.trim().toLowerCase();
        if (!s) return true;
        return (c.name || '').toLowerCase().includes(s) || (c.card_id || '').toLowerCase().includes(s);
    };
    const cardLabel = (c) => c ? `${c.name || c.card_id} (${c.card_id})` : '';

    useEffect(() => {
        api.get('/cards').then((r) => {
            const arr = Array.isArray(r.data) ? r.data : (r.data.cards || []);
            setCards(arr.filter((c) => c.card_id));
        }).catch(() => {});
        if (!isNew) {
            if (justCreatedRef.current) { justCreatedRef.current = false; return; }
            api.get(`/multi-location/${id}`).then((r) => {
                const d = r.data;
                setSlug(d.slug); setStatus(d.status); setName(d.name || '');
                setStyle(d.selection_style || 'visible_buttons');
                setContent({ heading: { ...emptyTT(), ...(d.content?.heading || {}) }, subtext: { ...emptyTT(), ...(d.content?.subtext || {}) } });
                setDesign((prev) => ({ ...prev, ...(d.design || {}) }));
                setLocations((d.locations || []).map((l) => ({ ...l, label: { ...emptyTT(), ...(l.label || {}) } })));
                setEntryCardIds(d.entry_card_ids || []);
            }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const orgId = useMemo(() => {
        const anyCard = [...locations.map((l) => l.destination_card_id), ...entryCardIds]
            .map((cid) => cardById[cid]).find(Boolean);
        return anyCard?.organization_id || null;
    }, [locations, entryCardIds, cardById]);

    const setLoc = (i, patch) => setLocations((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
    const addLocation = () => setLocations((ls) => [...ls, { choice_id: Math.random().toString(36).slice(2, 10), destination_card_id: '', label: emptyTT(), order: ls.length }]);
    const move = (i, dir) => setLocations((ls) => {
        const j = i + dir; if (j < 0 || j >= ls.length) return ls;
        const cp = [...ls];[cp[i], cp[j]] = [cp[j], cp[i]]; return cp.map((l, k) => ({ ...l, order: k }));
    });

    const toggleEntry = (cid) => {
        if (entryCardIds.includes(cid)) { setEntryCardIds((e) => e.filter((x) => x !== cid)); return; }
        const card = cardById[cid];
        const other = card?.multi_location_link_id;
        if (other && other !== linkId) {
            if (!window.confirm(`This card is currently an entry card for another Multi-Location Link. Move it here?`)) return;
        }
        setEntryCardIds((e) => [...e, cid]);
    };

    const payload = () => ({
        name, organization_id: orgId, selection_style: style, content, design,
        source_language: srcLang,
        locations: locations.filter((l) => l.destination_card_id).map((l, i) => ({ ...l, order: i })),
        entry_card_ids: entryCardIds,
    });

    const createDraft = async () => {
        const r = await api.post('/multi-location', payload());
        setLinkId(r.data.id); setSlug(r.data.slug); setStatus(r.data.status);
        justCreatedRef.current = true;
        return r.data.id;
    };

    const save = async (publish = false) => {
        if (!name.trim()) return toast.error(t('reviewShield.multiLocation.wizard.nameRequired'));
        setSaving(true);
        try {
            let lid = linkId;
            if (!lid) {
                lid = await createDraft();
            } else {
                await api.patch(`/multi-location/${lid}`, payload());
            }
            if (publish) {
                await api.patch(`/multi-location/${lid}`, { status: 'active' });
                setStatus('active');
            }
            toast.success(publish ? 'Published' : 'Saved');
            if (isNew) navigate(`/multi-location-links/${lid}`, { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const uploadAsset = async (kind, file) => {
        if (!name.trim()) return toast.error(t('reviewShield.multiLocation.wizard.nameRequired'));
        setSaving(true);
        try {
            // Seamlessly persist a draft first if this link isn't saved yet, then upload.
            const lid = linkId || await createDraft();
            const fd = new FormData(); fd.append('file', file);
            const r = await api.post(`/multi-location/${lid}/${kind === 'logo' ? 'logo' : 'background'}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setDesign((d) => ({ ...d, [kind === 'logo' ? 'logo_url' : 'background_image_url']: r.data.url }));
            toast.success('Uploaded');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    const masterUrl = slug ? `${window.location.origin}/m/${slug}` : null;
    const hasValidLocation = locations.some((l) => l.destination_card_id);

    const canContinue = () => {
        if (step === 1) return !!name.trim();
        if (step === 2) return hasValidLocation;
        return true;
    };
    const blockReason = () => {
        if (step === 1 && !name.trim()) return t('reviewShield.multiLocation.wizard.nameRequired');
        if (step === 2 && !hasValidLocation) return t('reviewShield.multiLocation.wizard.locationRequired');
        return null;
    };

    const STEP_LABELS = [
        t('reviewShield.multiLocation.wizard.step1'),
        t('reviewShield.multiLocation.wizard.step2'),
        t('reviewShield.multiLocation.wizard.step3'),
        t('reviewShield.multiLocation.wizard.step4'),
        t('reviewShield.multiLocation.wizard.step5'),
    ];
    const STEP_DESCS = [
        t('reviewShield.multiLocation.wizard.step1Desc'),
        t('reviewShield.multiLocation.wizard.step2Desc'),
        t('reviewShield.multiLocation.wizard.step3Desc'),
        t('reviewShield.multiLocation.wizard.step4Desc'),
        t('reviewShield.multiLocation.wizard.step5Desc'),
    ];

    return (
        <div className="max-w-3xl mx-auto p-6 space-y-5 text-slate-900 dark:text-slate-100" data-testid="mll-editor-page">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="text-2xl font-bold">
                    {isNew ? t('reviewShield.multiLocation.wizard.createTitle') : t('reviewShield.multiLocation.wizard.editTitle')}
                </h1>
                <div className="flex gap-2">
                    <Button variant="outline" data-testid="mll-cancel-btn" onClick={() => navigate('/multi-location-links')}>{t('common.cancel')}</Button>
                    <Button variant="outline" data-testid="mll-save-btn" disabled={saving} onClick={() => save(false)}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
                    </Button>
                </div>
            </div>

            {masterUrl && (
                <div className="flex items-center gap-3 text-sm rounded-xl bg-slate-100 dark:bg-slate-800 px-4 py-2 text-slate-700 dark:text-slate-200" data-testid="mll-master-link">
                    <span className="font-medium">Master link:</span>
                    <button onClick={() => { try { navigator.clipboard?.writeText(masterUrl); toast.success('Copied'); } catch { toast.success('Copied'); } }} className="text-sky-600 dark:text-sky-400 hover:underline flex items-center gap-1"><Copy className="w-3 h-3" />{masterUrl}</button>
                    <a href={masterUrl} target="_blank" rel="noreferrer" data-testid="mll-preview-link" className="ml-auto text-sky-600 dark:text-sky-400 hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" />Preview</a>
                </div>
            )}

            {/* Progress header */}
            <div className="space-y-3" data-testid="mll-wizard-progress">
                <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{STEP_LABELS[step - 1]}</span>
                    <span className="text-slate-500 dark:text-slate-400" data-testid="mll-step-indicator">
                        {t('reviewShield.multiLocation.wizard.stepOf', { current: step, total: TOTAL_STEPS })}
                    </span>
                </div>
                <Progress value={(step / TOTAL_STEPS) * 100} className="bg-slate-200 dark:bg-slate-700" />
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {STEP_LABELS.map((lbl, i) => (
                        <span key={i} data-testid={`mll-step-label-${i + 1}`}
                            className={`flex items-center gap-1.5 ${i + 1 === step ? 'text-sky-600 dark:text-sky-400 font-semibold' : i + 1 < step ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
                            <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] ${i + 1 === step ? 'bg-sky-600 text-white' : i + 1 < step ? 'bg-emerald-600 text-white' : 'bg-slate-300 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                                {i + 1 < step ? <Check className="w-2.5 h-2.5" /> : i + 1}
                            </span>
                            {lbl}
                        </span>
                    ))}
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{STEP_DESCS[step - 1]}</p>
            </div>

            {/* STEP 1 — Basic Info */}
            {step === 1 && (
                <Section title="Details">
                    <Input data-testid="mll-name" placeholder="Internal name (e.g. Baan Thai Locations)" value={name} onChange={(e) => setName(e.target.value)} />
                    <div className="pt-2 space-y-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="mll-autotranslate-hint">
                            {t('reviewShield.multiLocation.autoTranslateHint', 'French & Spanish are generated automatically after save.')}
                        </p>
                        {[['heading', 'Heading'], ['subtext', 'Subtext']].map(([f, lbl]) => (
                            <div key={f} className="space-y-1.5">
                                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{lbl} <span className="normal-case text-slate-400 dark:text-slate-500">({srcLang.toUpperCase()})</span></p>
                                <Input data-testid={`mll-${f}`} placeholder={lbl} value={content[f][srcLang] || ''}
                                    onChange={(e) => setContent((c) => ({ ...c, [f]: { ...c[f], [srcLang]: e.target.value } }))} />
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* STEP 2 — Locations */}
            {step === 2 && (
                <Section title="Locations / Attach Destination Cards">
                    {locations.map((loc, i) => (
                        <div key={loc.choice_id} data-testid={`mll-loc-${i}`} className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="flex-1">
                                    <CardCombobox cards={cards} value={loc.destination_card_id}
                                        onChange={(cid) => setLoc(i, { destination_card_id: cid })}
                                        matches={cardMatches} label={cardLabel} tid={`mll-loc-card-${i}`} />
                                </div>
                                <Button size="sm" variant="outline" onClick={() => move(i, -1)} data-testid={`mll-loc-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" onClick={() => move(i, 1)} data-testid={`mll-loc-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" onClick={() => setLocations((ls) => ls.filter((_, j) => j !== i))} data-testid={`mll-loc-remove-${i}`} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                            <Input data-testid={`mll-loc-label-${i}`} placeholder={`Label (${srcLang.toUpperCase()})`} value={loc.label[srcLang] || ''}
                                onChange={(e) => setLoc(i, { label: { ...loc.label, [srcLang]: e.target.value } })} />
                        </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={addLocation} data-testid="mll-add-location"><Plus className="w-4 h-4 mr-1" />Add location</Button>
                </Section>
            )}

            {/* STEP 3 — Entry Cards */}
            {step === 3 && (
                <Section title="Attach Entry Cards">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Cards that, when accessed, first show this selection page.</p>
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input data-testid="mll-entry-search" placeholder="Search by name or Card ID…" value={entryQuery}
                            onChange={(e) => setEntryQuery(e.target.value)} className="pl-8" />
                    </div>
                    <div className="max-h-64 overflow-auto space-y-1" data-testid="mll-entry-cards">
                        {cards.filter((c) => cardMatches(c, entryQuery)).map((c) => (
                            <label key={c.card_id} data-testid={`mll-entry-${c.card_id}`} className="flex items-center gap-2 text-sm py-1 cursor-pointer text-slate-700 dark:text-slate-200">
                                <input type="checkbox" checked={entryCardIds.includes(c.card_id)} onChange={() => toggleEntry(c.card_id)} />
                                <span>{c.name || c.card_id} <span className="text-slate-400 dark:text-slate-500">({c.card_id})</span></span>
                                {c.multi_location_link_id && c.multi_location_link_id !== linkId && <span className="text-[10px] text-amber-500">attached elsewhere</span>}
                            </label>
                        ))}
                        {cards.filter((c) => cardMatches(c, entryQuery)).length === 0 && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 py-2">No matching cards.</p>
                        )}
                    </div>
                </Section>
            )}

            {/* STEP 4 — Appearance */}
            {step === 4 && (
                <div className="space-y-4">
                    <Section title="Selection Style">
                        <div className="flex gap-3">
                            {[['visible_buttons', 'Visible Buttons (default)'], ['rolling_selector', 'Rolling Selector']].map(([v, lbl]) => (
                                <button key={v} type="button" data-testid={`mll-style-${v}`} onClick={() => setStyle(v)}
                                    className={`flex-1 py-2 rounded-xl border text-sm transition-colors ${style === v ? 'border-sky-500 bg-sky-500/10 text-sky-700 dark:text-sky-300 font-semibold' : 'border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>{lbl}</button>
                            ))}
                        </div>
                    </Section>

                    <Section title="Design / Branding">
                        <div className="flex flex-wrap gap-4 items-center">
                            <label className="text-sm flex items-center gap-2 cursor-pointer text-slate-700 dark:text-slate-200" data-testid="mll-upload-logo">
                                <Upload className="w-4 h-4" />Logo
                                <input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadAsset('logo', e.target.files[0])} />
                            </label>
                            {design.logo_url && <img src={design.logo_url} alt="" className="h-8 object-contain" />}
                            <label className="text-sm flex items-center gap-2 cursor-pointer text-slate-700 dark:text-slate-200" data-testid="mll-upload-bg">
                                <Upload className="w-4 h-4" />Wallpaper
                                <input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadAsset('background', e.target.files[0])} />
                            </label>
                            {design.background_image_url && <span className="text-xs text-emerald-600 dark:text-emerald-400">✓ set</span>}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                            <Select label="Logo size" value={design.logo_size} onChange={(v) => setDesign((d) => ({ ...d, logo_size: v }))} opts={['small', 'medium', 'large', 'very_large']} tid="mll-logo-size" />
                            <Select label="Display" value={design.display_mode} onChange={(v) => setDesign((d) => ({ ...d, display_mode: v }))} opts={['auto', 'light', 'dark']} tid="mll-display-mode" />
                            <Select label="Card style" value={design.card_style} onChange={(v) => setDesign((d) => ({ ...d, card_style: v }))} opts={['frosted', 'clear', 'solid']} tid="mll-card-style" />
                            <Select label="Button" value={design.button_style} onChange={(v) => setDesign((d) => ({ ...d, button_style: v }))} opts={['solid', 'outline']} tid="mll-button-style" />
                            <Select label="BG fit" value={design.background_behavior} onChange={(v) => setDesign((d) => ({ ...d, background_behavior: v }))} opts={['cover', 'contain', 'tile']} tid="mll-bg-behavior" />
                            <label className="flex flex-col gap-1">
                                <span className="text-xs text-slate-500 dark:text-slate-400">BG color</span>
                                <input type="color" data-testid="mll-bg-color" value={design.background_color || '#0f172a'} onChange={(e) => setDesign((d) => ({ ...d, background_color: e.target.value }))} className="h-9 w-full rounded" />
                            </label>
                        </div>
                    </Section>
                </div>
            )}

            {/* STEP 5 — Review & Publish */}
            {step === 5 && (
                <Section title={t('reviewShield.multiLocation.wizard.step5')}>
                    <div className="space-y-3 text-sm" data-testid="mll-review">
                        <ReviewRow label={t('reviewShield.multiLocation.wizard.reviewName')} value={name || t('reviewShield.multiLocation.wizard.reviewNone')} />
                        <ReviewRow label={t('reviewShield.multiLocation.wizard.reviewHeading')} value={content.heading[srcLang] || t('reviewShield.multiLocation.wizard.reviewNone')} />
                        <ReviewRow label={t('reviewShield.multiLocation.wizard.reviewStyle')} value={style === 'rolling_selector' ? 'Rolling Selector' : 'Visible Buttons'} />
                        <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">{t('reviewShield.multiLocation.wizard.reviewLocations')}</p>
                            {hasValidLocation ? (
                                <ul className="list-disc pl-5 space-y-0.5 text-slate-700 dark:text-slate-200">
                                    {locations.filter((l) => l.destination_card_id).map((l) => (
                                        <li key={l.choice_id}>{l.label[srcLang] || cardLabel(cardById[l.destination_card_id])} <span className="text-slate-400 dark:text-slate-500">→ {l.destination_card_id}</span></li>
                                    ))}
                                </ul>
                            ) : <p className="text-slate-400 dark:text-slate-500">{t('reviewShield.multiLocation.wizard.reviewNoLocations')}</p>}
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">{t('reviewShield.multiLocation.wizard.reviewEntryCards')}</p>
                            {entryCardIds.length ? (
                                <ul className="list-disc pl-5 space-y-0.5 text-slate-700 dark:text-slate-200">
                                    {entryCardIds.map((cid) => <li key={cid}>{cardLabel(cardById[cid]) || cid}</li>)}
                                </ul>
                            ) : <p className="text-slate-400 dark:text-slate-500">{t('reviewShield.multiLocation.wizard.reviewNoEntry')}</p>}
                        </div>
                    </div>
                    <div className="pt-3 flex justify-end">
                        <Button data-testid="mll-publish-btn" disabled={saving} onClick={() => save(true)} className="bg-sky-600 hover:bg-sky-500 text-white">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & Publish'}
                        </Button>
                    </div>
                </Section>
            )}

            {/* Wizard navigation */}
            <div className="space-y-2">
                {blockReason() && <p className="text-xs text-amber-600 dark:text-amber-400 text-right" data-testid="mll-block-reason">{blockReason()}</p>}
                <div className="flex items-center justify-between">
                    <Button variant="outline" data-testid="mll-back-btn" disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>
                        <ChevronLeft className="w-4 h-4 mr-1" />{t('reviewShield.multiLocation.wizard.back')}
                    </Button>
                    {step < TOTAL_STEPS && (
                        <Button data-testid="mll-continue-btn" disabled={!canContinue()} onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))} className="bg-sky-600 hover:bg-sky-500 text-white">
                            {t('reviewShield.multiLocation.wizard.continue')}<ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

const ReviewRow = ({ label, value }) => (
    <div className="flex gap-3">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 w-28 shrink-0 pt-0.5">{label}</span>
        <span className="text-slate-800 dark:text-slate-100 break-words">{value}</span>
    </div>
);

const Section = ({ title, children }) => (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-3">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h2>{children}
    </div>
);

const CardCombobox = ({ cards, value, onChange, matches, label, tid }) => {
    const [open, setOpen] = React.useState(false);
    const [q, setQ] = React.useState('');
    const selected = cards.find((c) => c.card_id === value);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={tid}
                    className="w-full h-9 flex items-center justify-between rounded-lg border border-slate-300 dark:border-slate-700 bg-transparent text-slate-900 dark:text-slate-100 px-3 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                    <span className={selected ? '' : 'text-slate-400 dark:text-slate-500'}>{selected ? label(selected) : 'Search cards…'}</span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[min(24rem,90vw)]" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Search by name or Card ID…" value={q} onValueChange={setQ} data-testid={`${tid}-search`} />
                    <CommandList>
                        <CommandEmpty>No matching cards.</CommandEmpty>
                        <CommandGroup>
                            {cards.filter((c) => matches(c, q)).slice(0, 50).map((c) => (
                                <CommandItem key={c.card_id} value={c.card_id}
                                    data-testid={`${tid}-opt-${c.card_id}`}
                                    onSelect={() => { onChange(c.card_id); setOpen(false); setQ(''); }}>
                                    <Check className={`w-4 h-4 mr-2 ${value === c.card_id ? 'opacity-100' : 'opacity-0'}`} />
                                    {label(c)}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

const Select = ({ label, value, onChange, opts, tid }) => (
    <label className="flex flex-col gap-1">
        <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
        <select data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)}
            className="h-9 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-2">
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    </label>
);
